import { useEffect, useRef, type ReactNode } from 'react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  className: string;
  labelledBy: string;
  id?: string;
  children: ReactNode;
}

/**
 * Native `<dialog>`: showModal()/close() follow `open`, which gives focus trapping, Escape and
 * `::backdrop`. Clicking the backdrop closes it.
 */
function NativeDialog({ open, onClose, className, labelledBy, id, children }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    } else if (!open && dialog.open) {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      id={id}
      className={className}
      aria-labelledby={labelledBy}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {open && children}
    </dialog>
  );
}

export function Drawer(props: Omit<DialogProps, 'className'>) {
  return <NativeDialog {...props} className="drawer" />;
}

export function Modal({
  wide = false,
  className,
  ...props
}: Omit<DialogProps, 'className'> & {
  /** Reading width for longer content, such as help articles. */
  wide?: boolean;
  /** A modifier for another width or layout, e.g. `modal--export`. */
  className?: string;
}) {
  return (
    <NativeDialog
      {...props}
      className={['modal', wide && 'modal--wide', className].filter(Boolean).join(' ')}
    />
  );
}
